<template>
  <div class="app">
    <main id="core-widget-scroll" ref="scrollContainer" class="app-scroll" :style="scrollContainerStyle">
      <div class="app-page" :style="appPageStyle">
        <RouterView :key="projectStore.projectRevision" />
      </div>
      <Footer />
    </main>
    <Dialog />
    <Toast />
    <BetaNotification />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import Footer from '@/components/partials/navigation/Footer.vue'
import Dialog from '@/components/partials/modals/Dialog.vue'
import Toast from '@/components/partials/display/Toast.vue'
import BetaNotification from '@/components/partials/display/BetaNotification.vue'
import { useFooterLayoutState } from '@/helpers/footer-layout-state'
import { useProjectStore } from '@/stores/project'

const route = useRoute()
const projectStore = useProjectStore()
const scrollContainer = ref<HTMLElement | null>(null)
const topInset = ref(0)
const { hasProjectSummaryContainer } = useFooterLayoutState()
let layoutObserver: MutationObserver | null = null

function syncTopInset(): void {
  const header = document.querySelector('.core-header') as HTMLElement | null
  const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0
  topInset.value = headerHeight
}

const scrollContainerStyle = computed(() => ({
  '--app-top-inset': `${topInset.value}px`,
  '--sticky-top-offset': '0px',
}))

const appPageStyle = computed(() => ({
  paddingBottom: hasProjectSummaryContainer.value ? '0px' : '81px',
}))

watch(
  () => route.fullPath,
  async () => {
    await nextTick()
    syncTopInset()
    scrollContainer.value?.scrollTo({ top: 0, left: 0 })
  },
  { immediate: true }
)

onMounted(() => {
  syncTopInset()
  window.addEventListener('resize', syncTopInset)

  if (scrollContainer.value) {
    layoutObserver = new MutationObserver(() => {
      syncTopInset()
    })
    layoutObserver.observe(scrollContainer.value, { childList: true, subtree: true })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncTopInset)
  layoutObserver?.disconnect()
})
</script>

<style lang="postcss" scoped>
.app {
  @apply flex flex-col;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.app-scroll {
  @apply flex flex-col;
  width: 100%;
  margin-top: var(--app-top-inset, 0px);
  height: calc(100dvh - var(--app-top-inset, 0px));
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.app-page {
  flex: 1 0 auto;
}

@supports not (height: 100dvh) {
  .app-scroll {
    height: calc(100vh - var(--app-top-inset, 0px));
  }
}
</style>
